'use client'

import { useEffect, useRef } from 'react'
import {
  flushClientTelemetry,
  isClientTelemetryReady,
  trackClientEvent,
} from '../components/client-telemetry'
import { verstuurTelemetryBeacon } from '../lib/telemetry-beacon'

/**
 * Herkent een ChunkLoadError: de browser draait nog JS van een vorige build en
 * probeert een chunk met een oude hash te laden die na een deploy niet meer
 * bestaat, of een module-import strandt in het iOS-resume-venster waarin
 * netwerk en service worker nog niet wakker zijn. Dekt de varianten die
 * webpack/Next en moderne browsers gooien bij een mislukte (lazy) module-import.
 *
 * LET OP - de drie global-error.tsx's (portal/backoffice/klant) bevatten een
 * bewust geïnlinede kopie van deze detector en de debounce hieronder (ze mogen
 * niet van de @streep/ui-bundle afhangen). Wijzig je hier de patronen, de
 * storage-key of het debounce-venster, werk die kopieën dan mee bij. De
 * drift-guard-test `apps/api/src/lib/__tests__/global-error-sync-drift.test.ts`
 * bewaakt de gedeelde literals.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const name = (error as { name?: string }).name ?? ''
  const message = (error as { message?: string }).message ?? ''
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  )
}

/**
 * Haalt het chunk-pad uit een chunk-foutmelding, voor de telemetrie: webpack-
 * fouten noemen de falende chunk-URL in de message ("Loading chunk 123 failed.
 * (error: https://.../_next/static/chunks/123-abc.js)"). Safari's generieke
 * "Importing a module script failed." bevat geen URL -> undefined. Het pad is
 * een hash-naam; terugvertalen naar module/route gaat via de build-manifest
 * van de app-versie die als ai.application.ver op het event zit.
 *
 * PII-guard: dit is het enige message-afgeleide dat de (ongeredigeerde)
 * beacon-route in mag. De extractie is daarom defensief-heuristisch: alleen
 * een /_next/static/...-pad tot een script-/css-extensie (geen host, geen
 * whitespace, geen query), input geklemd op 2000 en output op 200 tekens, en
 * een ')('-vangnet tegen melding-tekst die aan een extensie-loos pad vastplakt.
 * De drift-guard-test pint de regex-literal en het regex-gedrag vast.
 */
export function chunkPadUitFout(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined
  const message = ((error as { message?: string }).message ?? '').slice(0, 2000)
  const match = /\/_next\/static\/[\w@./()%[\]-]+?\.(?:m?js|css)/.exec(message)
  if (!match) return undefined
  const pad = match[0].slice(0, 200)
  return /\)\(/.test(pad) ? undefined : pad
}

const RELOAD_TS_KEY = 'streep:chunk-reload-at'
const RELOAD_DEBOUNCE_MS = 10_000

/**
 * Reload-beslissing, éénmalig per fout genomen (zie de ref in de hook). De
 * debounce-timestamp wordt METEEN bij een positieve beslissing geschreven:
 * alleen een geslaagde write geeft recht op een auto-reload. Is sessionStorage
 * geblokkeerd (lezen of schrijven faalt), dan valt de beslissing negatief uit
 * en toont de boundary de normale foutkaart met handmatige knop - zonder
 * debounce-administratie is een reload-loop anders niet uit te sluiten.
 */
function bepaalWillReload(error: unknown): boolean {
  if (!isChunkLoadError(error) || typeof window === 'undefined') return false
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_TS_KEY) ?? '0')
    if (Date.now() - last < RELOAD_DEBOUNCE_MS) return false
    window.sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()))
    return true
  } catch {
    return false
  }
}

/**
 * Herstelt een ChunkLoadError stil met één harde reload, zodat een gebruiker
 * die de app open had tijdens een deploy (of een PWA die uit de iOS-app-switcher
 * hervat) geen foutkaart ziet maar de nieuwste versie geserveerd krijgt. De
 * reload haalt het verse document plus de nieuwe chunk-hashes op.
 *
 * Debounce via sessionStorage: maximaal één auto-reload per ~10s. Komt de fout
 * NIET door een verouderde chunk (een echte bug, of het verse document faalt
 * opnieuw), dan wordt de reload onderdrukt en valt de boundary terug op de
 * normale foutkaart i.p.v. in een reload-loop te belanden.
 *
 * @param options.boundary - naam van de error-boundary ('app'/'segment'). Indien
 *          gezet trackt de hook het `chunk-reload`-event ZELF vlak vóór de
 *          reload; de boundary moet zijn eigen `trackClientEvent('chunk-reload')`
 *          dan weglaten. Zonder deze optie (bestaande callers) verandert er
 *          niets aan het track-gedrag. Reden: een reload gooit de SDK-batch
 *          weg, dus alleen track+flush+reload op één plek, in die volgorde,
 *          garandeert dat het event aankomt.
 *
 * @returns `true` als er daadwerkelijk een reload wordt uitgevoerd - de boundary
 *          toont dan een neutrale "nieuwe versie"-melding. `false` als het geen
 *          chunk-fout is, óf de reload door de debounce is onderdrukt; in beide
 *          gevallen hoort de boundary de normale foutkaart te tonen.
 */
export function useReloadOnChunkError(error: unknown, options?: { boundary?: string }): boolean {
  // De beslissing wordt per FOUT-identiteit vastgezet: een re-render tijdens
  // het reload-venster mag de kaart niet terug laten klappen naar de foutkaart
  // (en geen valse chunkFallback-exception in het boundary-effect triggeren);
  // een nieuwe fout na reset() krijgt wél een verse beslissing. De render-time
  // ref-write is oké: idempotent per fout en nergens anders in dezelfde render
  // gelezen.
  const beslissing = useRef<{ error: unknown; willReload: boolean } | null>(null)
  if (beslissing.current === null || beslissing.current.error !== error) {
    beslissing.current = { error, willReload: bepaalWillReload(error) }
  }
  const willReload = beslissing.current.willReload
  const boundary = options?.boundary
  // Welke chunk faalde (indien de melding het noemt) - maakt in de telemetrie
  // zichtbaar of het om een eigen lazy module gaat of om Next-route-chunks.
  // Alleen berekend waar het gebruikt wordt (boundary-optie gezet).
  const chunkPad = willReload && boundary ? chunkPadUitFout(error) : undefined

  useEffect(() => {
    if (!willReload || typeof window === 'undefined') return
    if (boundary) {
      // Track hier, vlak vóór de reload (de debounce-timestamp is al bij de
      // beslissing geschreven). SDK-eerst voor de rijke context (user/sessie),
      // met de niet-blokkerende onunloadFlush; is de lui-geladen SDK er nog
      // niet - het iOS-resume-scenario - dan dekt de sendBeacon-route het.
      // Precies één kanaal per reload, zodat de chunk-reload-churn in de
      // ops-rapportage niet dubbel telt.
      const props = { boundary, ...(chunkPad ? { chunk: chunkPad } : {}) }
      if (isClientTelemetryReady()) {
        trackClientEvent('chunk-reload', { ...props, via: 'sdk' })
        flushClientTelemetry()
      } else {
        verstuurTelemetryBeacon('chunk-reload', { ...props, via: 'beacon' })
      }
    }
    window.location.reload()
  }, [willReload, boundary, chunkPad])

  return willReload
}
